import React, { useEffect, useRef, useState } from 'react';
import { PauseIcon, PlayCircle, PlayIcon } from 'lucide-react';
import { useTranslation } from '../i18n';
import type { ResponsiveVideo } from '../data/orthodonticVideos';
type ResponsiveVideoBlockProps = {
  video: ResponsiveVideo;
  /** Short description of the video for screen readers. */
  label: string;
  className?: string;
  /**
   * Overrides the default frame sizing (mobile 9:16, desktop 16:9).
   * Must include both width and aspect-ratio classes.
   */
  frameClassName?: string;
  /** Shows a small, keyboard-accessible play/pause button. */
  showControls?: boolean;
  /** "contain" keeps the whole video frame visible (no cropping). */
  videoFit?: 'cover' | 'contain';
};
const DESKTOP_QUERY = '(min-width: 768px)';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const DEFAULT_FRAME = 'max-w-[320px] aspect-[9/16] md:max-w-none md:aspect-video';
function useMediaQuery(query: string): boolean | null {
  const [matches, setMatches] = useState<boolean | null>(null);
  useEffect(() => {
    const mql = window.matchMedia(query);
    const update = () => setMatches(mql.matches);
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, [query]);
  return matches;
}

/**
 * Muted, looping, inline video that plays only while visible.
 * Loads the desktop file on screens ≥ 768px and the mobile file below that.
 * With reduced motion enabled, it shows an early frame until the visitor presses play.
 */
export function ResponsiveVideoBlock({
  video,
  label,
  className = '',
  frameClassName = DEFAULT_FRAME,
  showControls = false,
  videoFit = 'cover'
}: ResponsiveVideoBlockProps) {
  const {
    lang
  } = useTranslation();
  const isDesktop = useMediaQuery(DESKTOP_QUERY);
  const prefersReducedMotion = useMediaQuery(REDUCED_MOTION_QUERY);
  const [userStarted, setUserStarted] = useState(false);
  const [userPaused, setUserPaused] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [inView, setInView] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const src = isDesktop === null ? null : isDesktop ? video.desktop : video.mobile;
  const canAutoplay = !userPaused && (prefersReducedMotion === false || userStarted);
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), {
      threshold: 0.35
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (inView && canAutoplay) {
      el.muted = true;
      el.play().catch(() => undefined);
    } else {
      el.pause();
    }
  }, [inView, canAutoplay, src]);
  const startPlayback = () => {
    setUserStarted(true);
    setUserPaused(false);
    const el = videoRef.current;
    if (el) {
      el.muted = true;
      el.play().catch(() => undefined);
    }
  };
  const togglePlayback = () => {
    if (isPlaying) {
      setUserPaused(true);
      videoRef.current?.pause();
    } else {
      startPlayback();
    }
  };
  const t = (en: string, ua: string, ru: string, ro: string) => lang === 'ua' ? ua : lang === 'ru' ? ru : lang === 'ro' ? ro : en;
  const playLabel = t('Play video', 'Відтворити відео', 'Воспроизвести видео', 'Redă videoclipul');
  const pauseLabel = t('Pause video', 'Призупинити відео', 'Приостановить видео', 'Pune videoclipul pe pauză');
  return <div ref={containerRef} className={`relative mx-auto w-full ${frameClassName} rounded-2xl overflow-hidden bg-forest-900 shadow-xl ${className}`} data-id="element-625" data-mp="13r222hcpr4v2">
      {src && <video key={src} ref={videoRef}
    // "#t=0.1" makes the browser show an early frame as the loading fallback
    src={`${src}#t=0.1`} muted loop playsInline disablePictureInPicture preload="metadata" aria-label={label} onPlay={() => setIsPlaying(true)} onPause={() => setIsPlaying(false)} className={`absolute inset-0 w-full h-full ${videoFit === 'contain' ? 'object-contain' : 'object-cover'}`} data-id="element-626" data-mp="3oh1284nio9n4" />}

      {prefersReducedMotion && !isPlaying && !userStarted && <button type="button" onClick={startPlayback} className="absolute inset-0 flex items-center justify-center bg-forest-900/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-4px] focus-visible:outline-white" aria-label={playLabel} data-id="element-627" data-mp="vizb09l8nb2l">
          <span className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-lg" data-id="element-628" data-mp="hk7ygrm0xxgk">
            <PlayCircle className="w-9 h-9 text-forest-700" aria-hidden="true" data-id="element-629" data-mp="377qh5r0k616d" />
          </span>
        </button>}

      {showControls && src && (!prefersReducedMotion || userStarted) && <button type="button" onClick={togglePlayback} aria-label={isPlaying ? pauseLabel : playLabel} className="absolute bottom-3 right-3 w-10 h-10 rounded-full bg-forest-900/70 text-white flex items-center justify-center backdrop-blur-sm transition-colors duration-150 hover:bg-forest-900/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white" data-id="element-630" data-mp="1hql523ndt66u">
          {isPlaying ? <PauseIcon className="w-4 h-4" aria-hidden="true" data-id="element-631" data-mp="25gp6q5h0evv7" /> : <PlayIcon className="w-4 h-4 ml-0.5" aria-hidden="true" data-id="element-632" data-mp="2aiekhsvm9nmz" />}
        </button>}
    </div>;
}